/**
 * MSW handler for the Payroll Team demo.
 *
 * `Team/Page.tsx` fetches `GET /team/` for real; without an answer, `Team/Demo` renders the
 * roster's error state rather than rows (the gap this PR's own description flags as a
 * follow-up). This intercepts that one call with wire-shaped `TeamMember` fixtures, modelled on
 * `Overview/demo/demoHandlers.ts` — the real query hook, the real request layer, the real URL,
 * paginated the way the server does so `usePayrollTeamRoster`'s accumulation loop is actually
 * exercised rather than assumed to work.
 */

// msw is a devDependency but this file is only used by Storybook, which includes devDependencies in its build
// eslint-disable-next-line import/no-extraneous-dependencies
import { http, HttpResponse } from 'msw';

import providerUnavailableResponse from 'modules/Payroll/common/demo/providerUnavailable';
import {
    ContractorCreateRequest,
    EmployeeCreateRequest,
    EntityKindEnum,
    HomeAddress,
    TeamMember,
    TeamMemberW9Status,
    WorkerConfirmRequest,
    WorkLocationList,
    WorkerDetail,
    WorkerTypeEnum,
    TeamFamily,
    TeamFamilyRequest,
} from 'services/payroll/api/generated/v1/model';

const TEAM_URL = '*/api/payroll/v1/team/';
const EMPLOYEE_DETAIL_URL = '*/api/payroll/v1/team/employees/:workerUuid/';
const CONTRACTOR_DETAIL_URL = '*/api/payroll/v1/team/contractors/:workerUuid/';
const EMPLOYEE_TERMINATE_URL = '*/api/payroll/v1/team/employees/:workerUuid/terminate/';
const CONTRACTOR_TERMINATE_URL = '*/api/payroll/v1/team/contractors/:workerUuid/terminate/';
const CONTRACTOR_REHIRE_URL = '*/api/payroll/v1/team/contractors/:workerUuid/rehire/';
// Not worker-type scoped: one endpoint invites employees and contractors alike.
const INVITE_URL = '*/api/payroll/v1/team/:workerUuid/invite/';
const WORK_LOCATIONS_URL = '*/api/payroll/v1/team/work-locations/';

/**
 * The contractor whose every write is refused, so the error path is demonstrable in the demo
 * rather than only describable. 422 is the provider saying no on its own terms.
 */
const REFUSING_CONTRACTOR_UUID = 'contractor-sofia-reyes';

/**
 * Every field a `TeamMember` row carries, at its least-interesting value. Fixtures and the invite
 * handler below spread over it so each states only what makes it that row — the wire shape is
 * twenty-nine keys wide and repeating it per fixture is what Sonar counts as duplication.
 */
const MEMBER_DEFAULTS: TeamMember = {
    business_name: '',
    can_be_paid: false,
    current_salary: null,
    display_name: '',
    email: '',
    entity_kind: EntityKindEnum.person,
    file_new_hire_report: null,
    has_ein: false,
    has_ssn: true,
    first_name: '',
    flsa_status: '',
    hourly_rate: null,
    is_owner: false,
    job_title: '',
    last_name: '',
    member_uuid: null,
    middle_initial: '',
    onboarding_state: 'sent',
    pay_schedule: null,
    pay_ytd: null,
    last_paid: null,
    payment_unit: '',
    preferred_first_name: '',
    start_date: null,
    status: 'inactive',
    termination_date: null,
    two_percent_shareholder: null,
    w9_status: TeamMemberW9Status.not_applicable,
    work_email: '',
    work_state: '',
    wage_type: '',
    worker_type: WorkerTypeEnum.employee,
    worker_uuid: '',
};

const buildMember = (overrides: Partial<TeamMember>): TeamMember => ({ ...MEMBER_DEFAULTS, ...overrides });

/** A mixed roster: active, invited (no salary yet) and terminated employees; hourly, fixed and never-paid contractors. */
const SEED_MEMBERS: readonly TeamMember[] = [
    buildMember({
        can_be_paid: true,
        current_salary: '98000.00',
        display_name: 'Ada Lovelace',
        email: 'ada.lovelace@example.com',
        first_name: 'Ada',
        is_owner: true,
        job_title: 'Engineering Lead',
        last_name: 'Lovelace',
        member_uuid: 'member-ada-lovelace',
        onboarding_state: 'confirmed',
        payment_unit: 'year',
        start_date: '2024-08-27',
        status: 'active',
        worker_uuid: 'employee-ada-lovelace',
    }),
    buildMember({
        can_be_paid: true,
        current_salary: '145000.00',
        display_name: 'Grace Hopper',
        email: 'grace.hopper@example.com',
        first_name: 'Grace',
        job_title: 'Staff Engineer',
        last_name: 'Hopper',
        onboarding_state: 'confirmed',
        payment_unit: 'year',
        start_date: '2025-02-14',
        status: 'active',
        worker_uuid: 'employee-grace-hopper',
    }),
    buildMember({
        current_salary: '92000.00',
        display_name: 'Margaret Hamilton',
        email: 'margaret.hamilton@example.com',
        first_name: 'Margaret',
        job_title: 'QA Engineer',
        last_name: 'Hamilton',
        // Real onboarding_state: worker finished their half, blocking payment until the employer confirms.
        onboarding_state: 'employer_review',
        payment_unit: 'year',
        start_date: '2026-09-08',
        worker_uuid: 'employee-margaret-hamilton',
    }),
    buildMember({
        current_salary: '88000.00',
        display_name: 'Omar Haddad',
        email: 'omar.haddad@example.com',
        first_name: 'Omar',
        last_name: 'Haddad',
        onboarding_state: 'employer_review',
        payment_unit: 'year',
        start_date: '2026-10-05',
        worker_uuid: 'employee-omar-haddad',
    }),
    buildMember({
        current_salary: '76000.00',
        display_name: 'Nina Patel',
        email: 'nina.patel@example.com',
        first_name: 'Nina',
        job_title: 'Marketing Analyst',
        last_name: 'Patel',
        onboarding_state: 'employer_review',
        payment_unit: 'year',
        start_date: '2026-10-12',
        worker_uuid: 'employee-nina-patel',
    }),
    buildMember({
        current_salary: '105000.00',
        display_name: 'Noor Patel',
        email: 'noor.patel@example.com',
        first_name: 'Noor',
        job_title: 'Product Designer',
        last_name: 'Patel',
        // Real onboarding_state: the worker opened the invitation link but hasn't finished yet.
        onboarding_state: 'opened',
        payment_unit: 'year',
        worker_uuid: 'employee-noor-patel',
    }),
    buildMember({
        display_name: 'Jordan Kim',
        email: 'jordan.kim@example.com',
        first_name: 'Jordan',
        hourly_rate: '55.00',
        last_name: 'Kim',
        // Unrecognized string — demos the adapter's `'invited'` fallback rather than a specific state.
        // Cast because the wire enum is closed and the fallback is exactly what cannot be typed.
        onboarding_state: 'awaiting_docusign' as TeamMember['onboarding_state'],
        payment_unit: 'hour',
        worker_uuid: 'employee-jordan-kim',
    }),
    buildMember({
        current_salary: '110000.00',
        display_name: 'Dorothy Vaughan',
        email: 'dorothy.vaughan@example.com',
        first_name: 'Dorothy',
        job_title: 'Product Manager',
        last_name: 'Vaughan',
        onboarding_state: 'confirmed',
        payment_unit: 'year',
        start_date: '2022-05-01',
        // Demo-only: AP-2186 documents `termination_date` as contractors-only, so a real employee
        // row never carries one and the terminated chip is unreachable on this tab. Kept because it
        // is the only way to see the chip at all — not evidence the live employee rail produces it.
        termination_date: '2026-06-30',
        worker_uuid: 'employee-dorothy-vaughan',
    }),
    buildMember({
        can_be_paid: true,
        display_name: 'Tim Cowherd',
        email: 'tim.cowherd@example.com',
        first_name: 'Tim',
        hourly_rate: '60.00',
        last_name: 'Cowherd',
        last_paid: '2026-09-12',
        onboarding_state: 'confirmed',
        pay_ytd: '9840.00',
        payment_unit: 'hour',
        start_date: '2025-09-01',
        status: 'active',
        w9_status: TeamMemberW9Status.received,
        wage_type: 'hourly',
        worker_type: WorkerTypeEnum.contractor,
        worker_uuid: 'contractor-tim-cowherd',
    }),
    buildMember({
        business_name: 'Natarajan Consulting LLC',
        can_be_paid: true,
        display_name: 'Priya Natarajan',
        email: 'priya.natarajan@example.com',
        entity_kind: EntityKindEnum.business,
        first_name: 'Priya',
        last_name: 'Natarajan',
        last_paid: '2026-09-01',
        onboarding_state: 'confirmed',
        pay_ytd: '18000.00',
        start_date: '2024-11-15',
        status: 'active',
        // Forward-dated, and matched exactly by her `DETAIL_OVERRIDES` entry — the list derives its
        // chip by comparing this to today, the detail reads `is_effective`, and the two must agree.
        termination_date: '2026-10-15',
        w9_status: TeamMemberW9Status.received,
        wage_type: 'fixed',
        worker_type: WorkerTypeEnum.contractor,
        worker_uuid: 'contractor-priya-natarajan',
    }),
    buildMember({
        display_name: 'Sofia Reyes',
        email: 'sofia.reyes@example.com',
        first_name: 'Sofia',
        hourly_rate: '45.00',
        last_name: 'Reyes',
        // Real onboarding_state: the invitation was sent, nothing else has happened yet.
        onboarding_state: 'sent',
        pay_ytd: '0.00',
        payment_unit: 'hour',
        w9_status: TeamMemberW9Status.not_started,
        wage_type: 'hourly',
        worker_type: WorkerTypeEnum.contractor,
        worker_uuid: 'contractor-sofia-reyes',
    }),
    buildMember({
        business_name: 'Fernandez Design Co.',
        display_name: 'Luis Fernandez',
        email: 'luis.fernandez@example.com',
        entity_kind: EntityKindEnum.business,
        // Real onboarding_state: the worker finished their half; not employer-review — that state is employee-focused in this demo.
        onboarding_state: 'worker_completed',
        pay_ytd: '0.00',
        payment_unit: 'month',
        w9_status: TeamMemberW9Status.not_started,
        wage_type: 'fixed',
        worker_type: WorkerTypeEnum.contractor,
        worker_uuid: 'contractor-luis-fernandez',
    }),
];

/**
 * The two create handlers are the only writes where a replay is not a no-op: re-terminating an
 * already-terminated worker changes nothing, but a second `MEMBERS.push` is how one invitation
 * becomes two workers, each owed its own tax form. The invite modals deliberately reuse one key
 * across a retry, so the demo has to honour that the way the real endpoint does.
 */
const SEEN_CREATE_KEYS = new Set<string>();

/** Home addresses recorded through the edit dialog, keyed by `worker_uuid`. */
const HOME_ADDRESSES = new Map<string, HomeAddress>();

/** True on the second and later use of a key. `requireKey` has already rejected a missing one. */
const isCreateReplay = (request: Request): boolean => {
    const key = request.headers.get('Idempotency-Key') ?? '';

    if (SEEN_CREATE_KEYS.has(key)) {
        return true;
    }

    SEEN_CREATE_KEYS.add(key);
    return false;
};

/**
 * The roster the write handlers mutate. Stories share module state, so `resetDemoRoster` puts the
 * seed back before each render — otherwise a delete or an edit in one story is still there when the
 * next one mounts.
 */
let MEMBERS: TeamMember[] = SEED_MEMBERS.map((member) => ({ ...member }));

let SPOUSE_UUID: string | null = null;
let OWNER_CONFIRMED = false;

export const resetDemoRoster = (): void => {
    MEMBERS = SEED_MEMBERS.map((member) => ({ ...member }));
    SEEN_CREATE_KEYS.clear();
    HOME_ADDRESSES.clear();
    SPOUSE_UUID = null;
    OWNER_CONFIRMED = false;
};

const FAMILY_URL = '*/api/payroll/v1/team/family/';

const currentFamily = (): TeamFamily => ({
    owner_worker_uuid: MEMBERS.find((member) => member.is_owner)?.worker_uuid ?? null,
    spouse_worker_uuid: SPOUSE_UUID,
    owner_confirmed: OWNER_CONFIRMED,
});

const familyHandler = http.get(FAMILY_URL, () => HttpResponse.json(currentFamily()));

/** Nobody matched on email or name: the state the owner warning exists for. */
export const unidentifiedOwnerFamilyHandler = http.get(FAMILY_URL, () =>
    HttpResponse.json(OWNER_CONFIRMED ? currentFamily() : { ...currentFamily(), owner_worker_uuid: null })
);

const familyUpdateHandler = http.put(FAMILY_URL, async ({ request }) => {
    const body = (await request.json()) as TeamFamilyRequest;
    MEMBERS = MEMBERS.map((member) => ({ ...member, is_owner: member.worker_uuid === body.owner_worker_uuid }));
    SPOUSE_UUID = body.spouse_worker_uuid;
    OWNER_CONFIRMED = true;
    return HttpResponse.json(currentFamily());
});

const countOf = (workerType: WorkerTypeEnum): number =>
    MEMBERS.filter((member) => member.worker_type === workerType).length;

/** Paginates the fixture the way the server does, so `usePayrollTeamRoster`'s accumulation loop is exercised for real. */
const teamHandler = http.get(TEAM_URL, ({ request }) => {
    const url = new URL(request.url);
    const limit = Number(url.searchParams.get('limit') ?? MEMBERS.length);
    const offset = Number(url.searchParams.get('offset') ?? 0);

    return HttpResponse.json({
        as_of: '2026-08-27T00:00:00Z',
        contractor_count: countOf(WorkerTypeEnum.contractor),
        count: MEMBERS.length,
        employee_count: countOf(WorkerTypeEnum.employee),
        results: MEMBERS.slice(offset, offset + limit),
    });
});

let commandSequence = 0;
/**
 * How many times each command has been polled — resolves to `confirmed` on the 2nd poll, so
 * Storybook shows the full pending → confirmed flow instead of hanging forever.
 */
const commandPollCounts = new Map<string, number>();

/**
 * The 202 envelope the real write answers with. `state: 'pending'` is deliberate: the command has
 * been accepted, not settled.
 */
const buildCommandAccepted = (commandType: string) => {
    commandSequence += 1;
    const commandUuid = `demo-command-${commandSequence}`;
    commandPollCounts.set(commandUuid, 0);

    return {
        command_type: commandType,
        command_uuid: commandUuid,
        poll_url: `/api/payroll/v1/commands/${commandUuid}/`,
        state: 'pending',
    };
};

const missingIdempotencyKey = () =>
    HttpResponse.json(
        {
            error: {
                code: 'IDEMPOTENCY_KEY_REQUIRED',
                message: 'This request is missing its idempotency key.',
                resolution: 'contact_support',
                retryable: false,
            },
        },
        { status: 400 }
    );

/**
 * `Idempotency-Key` is required on all eight writes and orval drops `in: header` parameters, so
 * asserting on it here is what makes the demo prove the mutator's header merge really works. A
 * handler that ignored the header would pass whether or not the fix were in place.
 */
const requireKey = (request: Request): boolean => request.headers.get('Idempotency-Key') !== null;

/**
 * Confirming flips `can_be_paid` and resolves `onboarding_state` to `confirmed`, so the row reads
 * `Active` once the roster refetches.
 *
 * The submitted employer fields are applied to the member too. `buildDetail` derives the detail
 * response from the member, so a handler that answered 202 without writing them back would show
 * the pre-edit values on the very refetch the confirmation triggers.
 */
/**
 * What a confirm changes. The employer-owned edits it used to accept (`job_title`, `start_date`,
 * `payment_unit`, `rate`) left the contract, so the demo stops pretending the call can edit them.
 *
 * `is_owner` is the one field the body still carries, and it is honoured even though today's
 * client sends an empty body: the demo models the contract, not one caller's current payload.
 * Omitted leaves the answer unchanged rather than asserting `false`, and it is employees-only —
 * the contract says the contractor rail ignores it.
 */
const confirmedFields = (member: TeamMember, body: WorkerConfirmRequest): Partial<TeamMember> => {
    const patch: Partial<TeamMember> = { can_be_paid: true, onboarding_state: 'confirmed' };

    if (body.is_owner !== undefined && member.worker_type === WorkerTypeEnum.employee) {
        patch.is_owner = body.is_owner;
    }

    return patch;
};

const confirmHandler = http.post('*/api/payroll/v1/team/:workerUuid/confirm/', async ({ params, request }) => {
    if (!requireKey(request)) {
        return missingIdempotencyKey();
    }

    const member = MEMBERS.find((candidate) => candidate.worker_uuid === params.workerUuid);

    if (member) {
        Object.assign(member, confirmedFields(member, ((await request.json()) ?? {}) as WorkerConfirmRequest));
    }

    return HttpResponse.json(buildCommandAccepted('confirm_worker'), { status: 202 });
});

/**
 * No frontend consumer yet — `useCommandPoll` was deleted for duplicating the generated
 * `usePayrollCommandRetrieve`. Kept because it is the demo-side half of whatever polling the
 * follow-up ticket adds, and MSW handlers are not subject to knip.
 */
const commandHandler = http.get('*/api/payroll/v1/commands/:commandUuid/', ({ params }) => {
    const commandUuid = params.commandUuid as string;
    const pollCount = commandPollCounts.get(commandUuid) ?? 0;
    commandPollCounts.set(commandUuid, pollCount + 1);

    return HttpResponse.json({
        chunk_completed: 1,
        chunk_total: 1,
        command_type: 'confirm_worker',
        command_uuid: commandUuid,
        confirmed_at: pollCount >= 1 ? '2026-09-01T00:00:00Z' : null,
        created_at: '2026-09-01T00:00:00Z',
        error_code: '',
        error_message: '',
        reason: '',
        result: 'unknown',
        sent_at: '2026-09-01T00:00:00Z',
        state: pollCount >= 1 ? 'confirmed' : 'sent',
        subject_id: commandUuid,
        subject_type: 'worker',
    });
});

/** The roster read failing outright — the state `Team/Demo` renders today with no handler at all. */
export const failingTeamHandler = http.get(TEAM_URL, () => providerUnavailableResponse());

const memberByUuid = (workerUuid: string): TeamMember | undefined =>
    MEMBERS.find((member) => member.worker_uuid === workerUuid);

/**
 * Detail payloads, keyed by `worker_uuid`. Only the fields that differ per worker live here; the
 * rest are filled by `buildDetail` from the roster row, so a detail can never disagree with the
 * list about a name or a status.
 *
 * These carry values the live API does not: `hyke/payroll/services/team.py` annotates
 * `WorkerDetail.pay_schedule` and `.termination` as the literal `None`, so production returns
 * null for both. The demo bundle is where the populated path is visible at all.
 */
/**
 * Where the demo's onboarded workers live. One address for all of them: the point of the card is
 * that a home address is a mailing address, distinct from the company premises above it, not that
 * each worker has a different one.
 */
const DEMO_HOME_ADDRESS: HomeAddress = {
    street_1: '2130 Fillmore St',
    street_2: 'Apt 4',
    city: 'San Francisco',
    state: 'CA',
    postal_code: '94115',
    courtesy_withholding: false,
    effective_date: '2024-02-01',
};

/** The address a demo employee has on file: the one saved this session, else the default. */
const homeAddressOf = (member: TeamMember): HomeAddress =>
    HOME_ADDRESSES.get(member.worker_uuid ?? '') ?? DEMO_HOME_ADDRESS;

const DETAIL_OVERRIDES: Record<string, Partial<WorkerDetail>> = {
    'employee-ada-lovelace': {
        pay_schedule: {
            anchor_pay_date: '2026-05-15',
            auto_payroll_enabled: true,
            custom_name: 'Semi-monthly payroll',
            day_1: 15,
            day_2: 31,
            frequency: 'semimonthly',
            is_primary: true,
            pay_schedule_uuid: 'schedule-demo-1',
            period_end_anchor: null,
        },
    },
    /** In effect: last day already passed, final wages paid off-cycle, no way back. */
    'employee-dorothy-vaughan': {
        termination: {
            effective_date: '2026-06-30',
            is_effective: true,
            cancelable: false,
            final_wages_paid_off_cycle: true,
        },
    },
    /**
     * Invited and nothing more, so the provider holds no home address for her yet. The card reads
     * "Not provided" here, which is the state an employer reviewing an invitation actually sees.
     */
    [REFUSING_CONTRACTOR_UUID]: {
        home_address: null,
    },
    /** Forward-dated: recorded, not yet in force, still cancelable. Matches her row's date exactly. */
    'contractor-priya-natarajan': {
        termination: {
            effective_date: '2026-10-15',
            is_effective: false,
            cancelable: true,
            final_wages_paid_off_cycle: false,
        },
    },
};

/**
 * The company's premises, as `GET /team/work-locations/` lists them — ours (`PayrollAddress` rows),
 * which is why a member may read them. The first is the address every detail below reports, so the
 * invite form's choice and the detail screen's "Work address" agree.
 */
const WORK_LOCATIONS: WorkLocationList = {
    results: [
        {
            uuid: 'location-demo-1',
            street_1: '548 Market St',
            street_2: '',
            city: 'San Francisco',
            state: 'CA',
            postal_code: '94103',
            phone_number: '4155550100',
            in_business_state: true,
            employee_count: 1,
        },
        {
            uuid: 'location-demo-2',
            street_1: '1 Main St',
            street_2: 'Suite 200',
            city: 'Oakland',
            state: 'CA',
            postal_code: '94612',
            phone_number: '5105550100',
            in_business_state: true,
            employee_count: 0,
        },
    ],
    business_states: ['CA'],
    business_states_source: 'state_of_operation',
    default_phone_number: '',
};

const workLocationsHandler = http.get(WORK_LOCATIONS_URL, () => HttpResponse.json(WORK_LOCATIONS));

/** The premises each invited worker was assigned, keyed by `worker_uuid`. Seeded workers all sit at the first. */
const INVITED_WORK_LOCATIONS = new Map<string, string>();

const workLocationFor = (member: TeamMember) => {
    const uuid = INVITED_WORK_LOCATIONS.get(member.worker_uuid ?? '');

    return WORK_LOCATIONS.results.find((location) => location.uuid === uuid) ?? WORK_LOCATIONS.results[0];
};

const buildDetail = (member: TeamMember): WorkerDetail => {
    const location = workLocationFor(member);

    return {
        as_of: '2026-08-27T00:00:00Z',
        bank_account_last_four: '8645',
        date_of_birth: '1994-07-23',
        home_address: homeAddressOf(member),
        pay_schedule: null,
        termination: null,
        withholdings_completed_at: null,
        work_address_city: location.city,
        work_address_postal_code: location.postal_code,
        work_address_state: location.state,
        work_address_street_1: location.street_1,
        work_address_street_2: location.street_2,
        work_location_uuid: location.uuid,
        current_work_location_uuid: location.uuid,
        work_state: '',
        works_from_home: false,
        worker: member,
        ...DETAIL_OVERRIDES[member.worker_uuid ?? ''],
    };
};

/** 404s an unknown uuid the way the typed routes do, so the detail error state is reachable. */
const buildDetailHandler = (url: string, workerType: WorkerTypeEnum) =>
    http.get(url, ({ params }) => {
        const member = memberByUuid(String(params.workerUuid));

        if (member?.worker_type !== workerType) {
            return HttpResponse.json({ error: { code: 'NOT_FOUND' } }, { status: 404 });
        }

        return HttpResponse.json(buildDetail(member));
    });

const employeeDetailHandler = buildDetailHandler(EMPLOYEE_DETAIL_URL, WorkerTypeEnum.employee);
const contractorDetailHandler = buildDetailHandler(CONTRACTOR_DETAIL_URL, WorkerTypeEnum.contractor);

/**
 * Every write answers `202 PayrollCommandAccepted` with `state: 'confirmed'`, which is what the
 * real endpoints do on the happy path: `command_ledger.execute()` calls the provider inline and
 * settles the row before the view responds. `poll_url` is present because the envelope carries it,
 * and `confirmed` is also why nothing here has to serve `GET /commands/{uuid}/`:
 * `refreshWhenCommandSettles` treats an already-terminal 202 as the outcome and never polls.
 * A demo that answered `pending` would need that route stubbed too.
 */
const commandAccepted = (commandType: string) =>
    HttpResponse.json(
        {
            command_uuid: `command-${commandType}-1`,
            command_type: commandType,
            state: 'confirmed',
            poll_url: `/api/payroll/v1/commands/command-${commandType}-1/`,
        },
        { status: 202 }
    );

/** The v1 error envelope, verbatim — the shape `payrollErrorMessage` actually reads. */
const refused = (message: string) =>
    HttpResponse.json(
        { error: { code: 'COMMAND_REFUSED', message, resolution: 'contact_support', retryable: false } },
        { status: 422 }
    );

const notFound = () =>
    HttpResponse.json(
        {
            error: {
                code: 'WORKER_NOT_FOUND',
                message: 'We could not find that worker.',
                resolution: 'self_serve',
                retryable: false,
            },
        },
        { status: 404 }
    );

/**
 * Mutates the in-memory roster so the demo shows a real loop: the write lands, the roster query is
 * invalidated, and the refetch reads the change back. Short-circuiting a hook to fixtures instead
 * would create a second code path that can look right while the wiring is wrong.
 */
const patchMember = (workerUuid: string, changes: Partial<TeamMember>): boolean => {
    const index = MEMBERS.findIndex((member) => member.worker_uuid === workerUuid);

    if (index === -1) {
        return false;
    }

    MEMBERS[index] = { ...MEMBERS[index], ...changes };

    return true;
};

const removeMember = (workerUuid: string): boolean => {
    const index = MEMBERS.findIndex((member) => member.worker_uuid === workerUuid);

    if (index === -1) {
        return false;
    }

    MEMBERS.splice(index, 1);

    return true;
};

const writeHandler = (
    url: string,
    workerType: WorkerTypeEnum,
    commandType: string,
    apply: (member: TeamMember, body: Record<string, unknown>) => void
) =>
    http.post(url, async ({ params, request }) => {
        if (!requireKey(request)) {
            return missingIdempotencyKey();
        }

        const workerUuid = String(params.workerUuid);
        const member = memberByUuid(workerUuid);

        if (member?.worker_type !== workerType) {
            return notFound();
        }

        if (workerUuid === REFUSING_CONTRACTOR_UUID) {
            return refused('Your payroll provider would not accept that change.');
        }

        apply(member, (await request.json()) as Record<string, unknown>);

        return commandAccepted(commandType);
    });

const updateHandler = (url: string, workerType: WorkerTypeEnum, commandType: string) =>
    http.patch(url, async ({ params, request }) => {
        if (!requireKey(request)) {
            return missingIdempotencyKey();
        }

        const workerUuid = String(params.workerUuid);
        const member = memberByUuid(workerUuid);

        if (member?.worker_type !== workerType) {
            return notFound();
        }

        if (workerUuid === REFUSING_CONTRACTOR_UUID) {
            return refused('Your payroll provider would not accept that change.');
        }

        const body = (await request.json()) as Record<string, string | undefined>;
        const first = body.first_name ?? member.first_name;
        const last = body.last_name ?? member.last_name;
        const business = body.business_name ?? member.business_name;

        patchMember(workerUuid, {
            first_name: first,
            last_name: last,
            business_name: business,
            email: body.email ?? member.email,
            job_title: body.job_title ?? member.job_title,
            // The provider recomposes the display name from whichever identity the entity uses.
            display_name: business || [first, last].filter(Boolean).join(' '),
        });

        return commandAccepted(commandType);
    });

const deleteHandler = (url: string, workerType: WorkerTypeEnum, commandType: string) =>
    http.delete(url, ({ params, request }) => {
        if (!requireKey(request)) {
            return missingIdempotencyKey();
        }

        const workerUuid = String(params.workerUuid);
        const member = memberByUuid(workerUuid);

        if (member?.worker_type !== workerType) {
            return notFound();
        }

        if (workerUuid === REFUSING_CONTRACTOR_UUID) {
            return refused('This contractor has been paid, so they are owed a 1099 and cannot be removed.');
        }

        removeMember(workerUuid);

        return commandAccepted(commandType);
    });

/**
 * Resending takes an empty body and changes no roster state — the row was already `sent`, and the
 * only observable effect is the success notification. It still validates like every other write, so
 * a caller that forgets `Idempotency-Key` or invents a worker uuid sees the same failure the real
 * API would give it rather than a false 202.
 */
const inviteHandler = http.post(INVITE_URL, ({ params, request }) => {
    if (!requireKey(request)) {
        return missingIdempotencyKey();
    }

    if (!memberByUuid(String(params.workerUuid))) {
        return notFound();
    }

    return commandAccepted('invite_worker');
});

const employeeUpdateHandler = updateHandler(EMPLOYEE_DETAIL_URL, WorkerTypeEnum.employee, 'employee.update');
const contractorUpdateHandler = updateHandler(CONTRACTOR_DETAIL_URL, WorkerTypeEnum.contractor, 'contractor.update');
const employeeDeleteHandler = deleteHandler(EMPLOYEE_DETAIL_URL, WorkerTypeEnum.employee, 'employee.delete');
const contractorDeleteHandler = deleteHandler(CONTRACTOR_DETAIL_URL, WorkerTypeEnum.contractor, 'contractor.delete');

const employeeTerminateHandler = writeHandler(
    EMPLOYEE_TERMINATE_URL,
    WorkerTypeEnum.employee,
    'employee.terminate',
    (member, body) => {
        patchMember(member.worker_uuid ?? '', {
            termination_date: String(body.effective_date),
            status: 'inactive',
        });
    }
);

const employeeHomeAddressHandler = writeHandler(
    '*/api/payroll/v1/team/employees/:workerUuid/home-address/',
    WorkerTypeEnum.employee,
    'employee.home_address',
    (member, body) => {
        const current = homeAddressOf(member);
        HOME_ADDRESSES.set(member.worker_uuid ?? '', {
            street_1: String(body.street_1),
            street_2: typeof body.street_2 === 'string' ? body.street_2 : '',
            city: String(body.city),
            state: String(body.state),
            postal_code: String(body.postal_code),
            courtesy_withholding: DEMO_HOME_ADDRESS.courtesy_withholding,
            effective_date: body.change === 'move' ? String(body.effective_date) : current.effective_date,
        });
    }
);

const contractorTerminateHandler = writeHandler(
    CONTRACTOR_TERMINATE_URL,
    WorkerTypeEnum.contractor,
    'contractor.terminate',
    (member, body) => {
        patchMember(member.worker_uuid ?? '', { termination_date: String(body.end_date), status: 'inactive' });
    }
);

const contractorRehireHandler = writeHandler(
    CONTRACTOR_REHIRE_URL,
    WorkerTypeEnum.contractor,
    'contractor.rehire',
    (member, body) => {
        patchMember(member.worker_uuid ?? '', {
            termination_date: null,
            start_date: String(body.start_date),
            status: 'active',
        });
    }
);

/**
 * Counts openings, not rows: `resetDemoRoster` puts the seed back but deliberately leaves this
 * climbing, so a uuid minted before a reset can never collide with one minted after it.
 */
let inviteSequence = 0;

/**
 * The invite send (AP-2189) — `POST /team/employees/`, which is self-onboarding only and so needs
 * no flag; there is no separate invite endpoint. `requireKey` applies here as to the eight writes
 * above: the spec marks `Idempotency-Key` required on this endpoint too, and this is the one write
 * where a replay would create a second billable employee rather than repeat a change.
 *
 * The new row lands as `sent` — the roster only knows an invitation went out, never that the worker
 * has done anything with it.
 */
const employeeCreateHandler = http.post('*/api/payroll/v1/team/employees/', async ({ request }) => {
    if (!requireKey(request)) {
        return missingIdempotencyKey();
    }

    if (isCreateReplay(request)) {
        return commandAccepted('invite_worker');
    }

    const body = (await request.json()) as EmployeeCreateRequest;
    inviteSequence += 1;
    const workerUuid = `employee-demo-invite-${inviteSequence}`;

    if (body.work_location_uuid) {
        INVITED_WORK_LOCATIONS.set(workerUuid, body.work_location_uuid);
    }

    MEMBERS.push(
        buildMember({
            current_salary: body.payment_unit === 'year' ? body.rate : null,
            display_name: `${body.first_name} ${body.last_name}`,
            email: body.email,
            first_name: body.first_name,
            hourly_rate: body.payment_unit === 'hour' ? body.rate : null,
            job_title: body.job_title ?? '',
            last_name: body.last_name,
            payment_unit: body.payment_unit,
            start_date: body.start_date,
            worker_uuid: workerUuid,
        })
    );

    // `invite_worker`, not `invite_employee` — see `Capability.INVITE_WORKER` in the backend's
    // `payroll/providers/capabilities.py`. Nothing reads `command_type` yet; the command poll will.
    return commandAccepted('invite_worker');
});

let contractorInviteSequence = 0;

/**
 * The contractor half of the same invite (AP-2189) — `POST /team/contractors/`, self-onboarding
 * only like the employee create. `requireKey` applies for the same reason it does there: a replay
 * is how one contractor becomes two, each owed its own 1099.
 *
 * The new row lands as `sent`, and `not_started` is the honest W-9 state for someone who has not
 * begun onboarding.
 */
const contractorCreateHandler = http.post('*/api/payroll/v1/team/contractors/', async ({ request }) => {
    if (!requireKey(request)) {
        return missingIdempotencyKey();
    }

    if (isCreateReplay(request)) {
        return commandAccepted('invite_worker');
    }

    const body = (await request.json()) as ContractorCreateRequest;
    contractorInviteSequence += 1;
    const isBusiness = body.contractor_type === 'business';

    MEMBERS.push(
        buildMember({
            business_name: isBusiness ? body.business_name ?? '' : '',
            display_name: isBusiness ? body.business_name ?? '' : `${body.first_name ?? ''} ${body.last_name ?? ''}`,
            email: body.email,
            entity_kind: isBusiness ? EntityKindEnum.business : EntityKindEnum.person,
            first_name: body.first_name ?? '',
            hourly_rate: body.wage_type === 'hourly' ? body.hourly_rate ?? null : null,
            last_name: body.last_name ?? '',
            // Derived, never independent: the two can't disagree. A blank `wage_type` means unknown.
            payment_unit: body.wage_type === 'hourly' ? 'hour' : 'month',
            start_date: body.start_date,
            w9_status: TeamMemberW9Status.not_started,
            wage_type: body.wage_type,
            worker_type: WorkerTypeEnum.contractor,
            worker_uuid: `contractor-demo-invite-${contractorInviteSequence}`,
        })
    );

    // `invite_worker` here too — `Capability.INVITE_WORKER` is the only invite capability the
    // backend defines; there is no `invite_contractor`.
    return commandAccepted('invite_worker');
});

const allDemoHandlers = {
    team: teamHandler,
    workLocations: workLocationsHandler,
    invite: inviteHandler,
    employeeCreate: employeeCreateHandler,
    contractorCreate: contractorCreateHandler,
    confirm: confirmHandler,
    command: commandHandler,
    employeeDetail: employeeDetailHandler,
    contractorDetail: contractorDetailHandler,
    // The eight writes AP-2186 added. Each mutates MEMBERS, so the demo shows the whole loop:
    // write, invalidate, refetch, changed row.
    employeeTerminate: employeeTerminateHandler,
    employeeHomeAddress: employeeHomeAddressHandler,
    contractorTerminate: contractorTerminateHandler,
    contractorRehire: contractorRehireHandler,
    employeeUpdate: employeeUpdateHandler,
    contractorUpdate: contractorUpdateHandler,
    employeeDelete: employeeDeleteHandler,
    contractorDelete: contractorDeleteHandler,
    family: familyHandler,
    familyUpdate: familyUpdateHandler,
};

export default allDemoHandlers;
